/**
 * Shared UI primitives — extracted from App.jsx (Task 4 of modularisation)
 *
 * Exports: Field, Chip, AIBox, Badge, FilterBar, MiniEditor, MarkdownView,
 *          LibraryCard, MediaPickerModal, renderMarkdown
 *
 * Also exports: GenerateImagePanel, IMAGE_PROVIDERS, IMAGE_PROVIDER_LABEL
 * (tightly coupled to MediaPickerModal; re-exported by App.jsx for use elsewhere)
 */

import React, { useState, useEffect, useRef } from "react";
import { C, S, BRAND_VOICE_TONES, BRAND_VISUAL_STYLES, BRAND_FONT_OPTIONS, BRAND_CTA_SUGGESTIONS, SEED_TOPIC_CATEGORIES, slugifyTopicCategoryId, normaliseTopicWeights, validateTopicCategories, CATEGORY_BADGE } from "./constants.js";
import { useIsMobile } from "./hooks.js";
import { store } from "../utils/store.js";
import { ai } from "../utils/api.js";
import { getWorkspaceId } from "../utils/supabase.js";

// ── CW_TYPE_COL — used by LibraryCard ────────────────────────────────────────
export const CW_TYPE_COL = {
  blog: "#6366f1",
  email: "#f59e0b",
  announcement: "#4285f4",
  "menu-desc": "#10b981",
  press: "#8b5cf6",
  about: "#c4824a",
};

// ── Image provider config — used by GenerateImagePanel / MediaPickerModal ─────
export const IMAGE_PROVIDERS = [
  { id: "standard", label: "Flux",     sub: "Free · no key needed",         color: C.t2      },
  { id: "hd",       label: "DALL-E 3", sub: "$0.04/img · OPENAI_API_KEY",   color: "#10b981" },
  { id: "imagen",   label: "Imagen 3", sub: "~$0.04/img · GEMINI_API_KEY",  color: "#4285f4" },
];
export const IMAGE_PROVIDER_LABEL = { standard: "Flux", hd: "DALL-E 3", imagen: "Imagen 3" };

// ── mdToHtml — used by MiniEditor ─────────────────────────────────────────────
export function mdToHtml(md) {
  if (!md) return "";
  const lines = md.split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const l = lines[i];
    const inline = t => t.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
      .replace(/\*\*\*(.*?)\*\*\*/g,"<strong><em>$1</em></strong>")
      .replace(/\*\*(.*?)\*\*/g,"<strong>$1</strong>")
      .replace(/\*(.*?)\*/g,"<em>$1</em>")
      .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g,'<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    if (l.startsWith("### ")) { out.push(`<h3>${inline(l.slice(4))}</h3>`); }
    else if (l.startsWith("## "))  { out.push(`<h2>${inline(l.slice(3))}</h2>`); }
    else if (l.startsWith("# "))   { out.push(`<h1>${inline(l.slice(2))}</h1>`); }
    else if (/^!\[.*?\]\(/.test(l)) {
      const m = l.match(/^!\[([^\]]*)\]\(([^)]+)\)/);
      if (m) out.push(`<img src="${m[2]}" alt="${m[1]}" style="width:100%;border-radius:8px;margin:12px 0;display:block;max-height:420px;object-fit:cover;">`);
    }
    else if (l.startsWith("- ") || l.startsWith("* ")) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith("- ") || lines[i].startsWith("* "))) {
        items.push(`<li>${inline(lines[i].slice(2))}</li>`); i++;
      }
      out.push(`<ul>${items.join("")}</ul>`); continue;
    } else if (/^\d+\.\s/.test(l)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\d+\.\s/,""))}</li>`); i++;
      }
      out.push(`<ol>${items.join("")}</ol>`); continue;
    } else if (!l.trim()) { out.push("<p><br></p>"); }
    else { out.push(`<p>${inline(l)}</p>`); }
    i++;
  }
  return out.join("");
}

// ── parseInline — used by MarkdownView ───────────────────────────────────────
function parseInline(text) {
  return text
    .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
    .replace(/\*\*\*(.*?)\*\*\*/g,"<strong><em>$1</em></strong>")
    .replace(/\*\*(.*?)\*\*/g,"<strong>$1</strong>")
    .replace(/\*(.*?)\*/g,"<em>$1</em>")
    .replace(/`([^`]+)`/g,`<code style="background:#edf0f5;padding:1px 5px;border-radius:3px;font-family:monospace;font-size:12px;color:#000">$1</code>`);
}

// ── inlineMarkdown — used by renderMarkdown ───────────────────────────────────
function inlineMarkdown(text) {
  const parts = [];
  const re = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|\[([^\]]+)\]\(([^)\s]+)\))/g;
  let last = 0, m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[2] !== undefined) parts.push(<strong key={m.index} style={{ fontWeight:700 }}>{m[2]}</strong>);
    else if (m[3] !== undefined) parts.push(<em key={m.index}>{m[3]}</em>);
    else if (m[4] !== undefined) parts.push(<code key={m.index} style={{ background:"rgba(0,0,0,0.08)", padding:"1px 4px", borderRadius:3, fontSize:"0.95em", fontFamily:"monospace" }}>{m[4]}</code>);
    else if (m[5] !== undefined) parts.push(<a key={m.index} href={m[6]} target="_blank" rel="noopener noreferrer" style={{ color:"#2563eb", textDecoration:"underline" }}>{m[5]}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1 && typeof parts[0] === "string" ? parts[0] : parts;
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────

export function Field({ label, children, half }) {
  return (
    <div style={{ marginBottom: 16, ...(half ? { gridColumn: "span 1" } : {}) }}>
      <label style={S.label}>{label}</label>
      {children}
    </div>
  );
}

export function Chip({ label, active, color, onClick }) {
  const col = color || C.accent;
  return (
    <button onClick={onClick} style={{ ...S.btnGhost, ...(active ? { background: `${col}18`, color: col, borderColor: col } : {}), padding: "8px 16px", fontSize: 13 }}>
      {label}
    </button>
  );
}

export function AIBox({ content, loading, error }) {
  const [copied, setCopied] = useState(false);
  if (loading) return (
    <div style={{ ...S.card, padding: 24, textAlign: "center", marginTop: 20 }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: C.accent, fontSize: 13 }}>
        <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>✦</span>
        Generating with AI…
      </div>
    </div>
  );
  if (error) return <div style={{ ...S.card, padding: 16, marginTop: 20, borderColor: C.danger }}><div style={{ color: C.danger, fontSize: 13 }}>Error: {error}</div></div>;
  if (!content) return null;
  const copy = () => { navigator.clipboard.writeText(content); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div style={{ ...S.card, padding: 24, marginTop: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <span style={{ fontSize: 11, color: C.t3, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>Generated Output</span>
        <button onClick={copy} style={{ ...S.btnGhost, padding: "4px 12px", fontSize: 12 }}>{copied ? "✓ Copied" : "⎘ Copy"}</button>
      </div>
      <div style={{ color: C.text, fontSize: 13.5, lineHeight: 1.75 }}>{renderMarkdown(content)}</div>
    </div>
  );
}

export function Badge({ label, color }) {
  return <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: `${color}20`, color: color, fontWeight: 600, letterSpacing: "0.04em" }}>{label}</span>;
}

// Reusable search + date-range filter bar.
// Props: items[], searchFields[], dateField, placeholder, onChange(filteredItems, isActive)
export function FilterBar({ items = [], searchFields = ["title","name"], dateField = "savedAt", placeholder = "Search…", onChange }) {
  const [q, setQ]         = useState("");
  const [range, setRange] = useState("all");
  const [from, setFrom]   = useState("");
  const [to, setTo]       = useState("");

  useEffect(() => {
    let r = [...items];
    if (q.trim()) {
      const lq = q.toLowerCase();
      r = r.filter(item => searchFields.some(f => String(item[f] || "").toLowerCase().includes(lq)));
    }
    if (range !== "all") {
      const now  = new Date();
      const cuts = { today: new Date(now.getFullYear(), now.getMonth(), now.getDate()), "7d": new Date(now - 7*864e5), "30d": new Date(now - 30*864e5), "60d": new Date(now - 60*864e5) };
      const cutoff  = cuts[range] ?? (range === "custom" && from ? new Date(from) : null);
      const endDate = (range === "custom" && to) ? new Date(to + "T23:59:59") : null;
      if (cutoff) r = r.filter(item => { const d = new Date(item[dateField]); return endDate ? d >= cutoff && d <= endDate : d >= cutoff; });
    }
    onChange(r, !!(q.trim() || range !== "all"));
  }, [q, range, from, to, items]); // eslint-disable-line

  const active = !!(q.trim() || range !== "all");

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:20 }}>
      {/* Search */}
      <div style={{ position:"relative" }}>
        <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:C.t3, fontSize:16, pointerEvents:"none" }}>⌕</span>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder}
          style={{ ...S.input, paddingLeft:32, paddingRight: q ? 32 : undefined }} />
        {q && <button onClick={() => setQ("")}
          style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:C.t3, cursor:"pointer", fontSize:16, lineHeight:1 }}>✕</button>}
      </div>
      {/* Date presets */}
      <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
        <span style={{ fontSize:11, color:C.t3, flexShrink:0 }}>Date:</span>
        {[["all","All time"],["today","Today"],["7d","Last 7 days"],["30d","Last 30 days"],["60d","Last 60 days"],["custom","Custom"]].map(([v,l]) => (
          <button key={v} onClick={() => setRange(v)}
            style={{ padding:"4px 12px", borderRadius:16, border:`1px solid ${range===v?C.accent:C.border}`, background:range===v?`${C.accent}15`:"transparent", color:range===v?C.accent:C.t2, fontSize:11, fontWeight:range===v?700:400, cursor:"pointer", fontFamily:"inherit", transition:"all 0.1s" }}>
            {l}
          </button>
        ))}
        {active && (
          <button onClick={() => { setQ(""); setRange("all"); setFrom(""); setTo(""); }}
            style={{ padding:"4px 10px", borderRadius:16, border:`1px solid ${C.danger}40`, background:`${C.danger}10`, color:C.danger, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>
            ✕ Clear filters
          </button>
        )}
      </div>
      {/* Custom date pickers */}
      {range === "custom" && (
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          <span style={{ fontSize:11, color:C.t3 }}>From</span>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            style={{ ...S.input, padding:"5px 10px", fontSize:12, width:"auto" }} />
          <span style={{ fontSize:11, color:C.t3 }}>To</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            style={{ ...S.input, padding:"5px 10px", fontSize:12, width:"auto" }} />
        </div>
      )}
    </div>
  );
}

// ── Mini WYSIWYG editor ───────────────────────────────────────────────────────
export function MiniEditor({ initialMarkdown, onChange, profile, imageQuality = "standard" }) {
  const ref        = useRef(null);
  const ready      = useRef(false);
  const [srcMode,      setSrcMode]      = useState(false);
  const [srcHtml,      setSrcHtml]      = useState("");
  const [showImgPicker, setShowImgPicker] = useState(false);
  const pendingImg  = useRef(null); // the <img> element that was clicked (replace mode)
  const savedRange  = useRef(null); // saved cursor position for insert mode

  useEffect(() => {
    if (ref.current && !ready.current) {
      ref.current.innerHTML = mdToHtml(initialMarkdown);
      ready.current = true;
    }
  }, []);

  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg ?? null);
  };

  const prettyHtml = (html) => {
    const BLOCK = /^(html|head|body|div|p|h[1-6]|ul|ol|li|table|thead|tbody|tr|th|td|blockquote|pre|figure|figcaption|img|br|hr|section|article|header|footer|nav|main|aside)$/i;
    let out = "", indent = 0;
    // Tokenise into tags and text
    const tokens = html.split(/(<[^>]+>)/g).filter(t => t !== "");
    for (const tok of tokens) {
      if (!tok.startsWith("<")) {
        const t = tok.replace(/\n/g, " ").trim();
        if (t) out += "  ".repeat(indent) + t + "\n";
        continue;
      }
      const isClose   = tok.startsWith("</");
      const isSelf    = /\/>$/.test(tok) || /^<(br|hr|img|input|meta|link)/i.test(tok);
      const tag       = (tok.match(/^<\/?([a-z0-9]+)/i) || [])[1] || "";
      const isBlock   = BLOCK.test(tag);
      if (isClose && isBlock) indent = Math.max(0, indent - 1);
      if (isBlock) out += "  ".repeat(indent) + tok + "\n";
      else         out += tok; // inline tags stay inline
      if (!isClose && !isSelf && isBlock) indent++;
    }
    return out.replace(/\n{3,}/g, "\n\n").trim();
  };

  const enterSource = () => {
    const html = ref.current?.innerHTML || "";
    setSrcHtml(prettyHtml(html));
    setSrcMode(true);
  };

  const exitSource = () => {
    if (ref.current) {
      ref.current.innerHTML = srcHtml;
      onChange(srcHtml);
    }
    setSrcMode(false);
  };

  const TOOLS = [
    { label:"B",  title:"Bold",      cmd:"bold",        s:{fontWeight:700} },
    { label:"I",  title:"Italic",    cmd:"italic",      s:{fontStyle:"italic"} },
    { label:"U",  title:"Underline", cmd:"underline",   s:{textDecoration:"underline"} },
    { sep:true },
    { label:"H1", title:"Heading 1", cmd:"formatBlock", arg:"h1", s:{fontWeight:700} },
    { label:"H2", title:"Heading 2", cmd:"formatBlock", arg:"h2", s:{fontWeight:700} },
    { label:"H3", title:"Heading 3", cmd:"formatBlock", arg:"h3", s:{fontWeight:700} },
    { label:"¶",  title:"Paragraph", cmd:"formatBlock", arg:"p" },
    { sep:true },
    { label:"• List",  title:"Bullet list",   cmd:"insertUnorderedList" },
    { label:"1. List", title:"Numbered list", cmd:"insertOrderedList" },
    { sep:true },
    { label:"↩", title:"Undo", cmd:"undo" },
    { label:"↪", title:"Redo", cmd:"redo" },
  ];

  const toolBtn = (active) => ({
    padding:"4px 9px", borderRadius:5, border:`1px solid ${active ? C.accent : "transparent"}`,
    background: active ? `${C.accent}15` : "transparent",
    color: active ? C.accent : C.t2,
    fontSize:12, cursor:"pointer", fontFamily:"inherit", lineHeight:1.4, transition:"all 0.1s",
  });

  const handleEditorClick = (e) => {
    if (e.target.tagName === "IMG") {
      e.preventDefault();
      pendingImg.current = e.target;
      setShowImgPicker(true);
    }
  };

  const openInsertPicker = () => {
    // Save current cursor position so we can insert at the right spot after the modal closes
    const sel = window.getSelection();
    if (sel?.rangeCount) savedRange.current = sel.getRangeAt(0).cloneRange();
    pendingImg.current = null; // signals insert mode (not replace)
    setShowImgPicker(true);
  };

  return (
    <div style={{ border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
      {/* Toolbar */}
      <div style={{ display:"flex", alignItems:"center", gap:2, padding:"8px 12px", background:C.s2, borderBottom:`1px solid ${C.border}`, flexWrap:"wrap" }}>
        {!srcMode && TOOLS.map((t, i) => t.sep ? (
          <div key={i} style={{ width:1, height:18, background:C.border, margin:"0 4px", flexShrink:0 }} />
        ) : (
          <button key={i} title={t.title}
            onMouseDown={e => { e.preventDefault(); exec(t.cmd, t.arg); }}
            style={{ padding:"4px 9px", borderRadius:5, border:`1px solid transparent`, background:"transparent", color:C.t2, fontSize:12, cursor:"pointer", fontFamily:"inherit", ...(t.s||{}), lineHeight:1.4, transition:"all 0.1s" }}
            onMouseOver={e => { e.currentTarget.style.background=C.s3; e.currentTarget.style.color=C.text; e.currentTarget.style.borderColor=C.border; }}
            onMouseOut={e  => { e.currentTarget.style.background="transparent"; e.currentTarget.style.color=C.t2; e.currentTarget.style.borderColor="transparent"; }}>
            {t.label}
          </button>
        ))}
        {!srcMode && <div style={{ width:1, height:18, background:C.border, margin:"0 4px", flexShrink:0 }} />}
        {!srcMode && (
          <button title="Insert image from Media Library"
            onMouseDown={e => e.preventDefault()}
            onClick={openInsertPicker}
            style={{ padding:"4px 9px", borderRadius:5, border:"1px solid transparent", background:"transparent", color:C.t2, fontSize:12, cursor:"pointer", fontFamily:"inherit", lineHeight:1.4, transition:"all 0.1s" }}
            onMouseOver={e => { e.currentTarget.style.background=C.s3; e.currentTarget.style.color=C.text; e.currentTarget.style.borderColor=C.border; }}
            onMouseOut={e  => { e.currentTarget.style.background="transparent"; e.currentTarget.style.color=C.t2; e.currentTarget.style.borderColor="transparent"; }}>
            ⊞ Media
          </button>
        )}
        {!srcMode && <div style={{ width:1, height:18, background:C.border, margin:"0 4px", flexShrink:0 }} />}
        {/* Source toggle */}
        <button title={srcMode ? "Back to rich text" : "Edit HTML source"}
          onMouseDown={e => e.preventDefault()}
          onClick={srcMode ? exitSource : enterSource}
          style={toolBtn(srcMode)}>
          {"</>"}
        </button>
      </div>

      {/* Image hover styles — injected once */}
      <style>{`
        .kurb-mini-editor img { cursor:pointer; border-radius:6px; transition:outline 0.15s; position:relative; }
        .kurb-mini-editor img:hover { outline:3px solid #6366f1; outline-offset:2px; }
        .kurb-mini-editor .kurb-img-wrap { position:relative; display:block; }
        .kurb-mini-editor .kurb-img-wrap::after { content:"✦ Replace / Generate"; position:absolute; top:10px; left:10px; background:rgba(99,102,241,0.88); color:#fff; font-size:11px; font-weight:600; padding:4px 10px; border-radius:5px; pointer-events:none; opacity:0; transition:opacity 0.15s; font-family:system-ui,sans-serif; }
        .kurb-mini-editor .kurb-img-wrap:hover::after { opacity:1; }
      `}</style>

      {/* Rich text area — hidden in source mode so it keeps its content */}
      <div
        ref={ref}
        contentEditable={!srcMode}
        suppressContentEditableWarning
        className="kurb-mini-editor"
        onInput={e => onChange(e.currentTarget.innerHTML)}
        onClick={handleEditorClick}
        style={{ minHeight:480, padding:"24px 32px", outline:"none", lineHeight:1.8, fontSize:14, color:C.text, background:C.s1, overflowY:"auto", display: srcMode ? "none" : "block" }}
      />
      {/* HTML source textarea */}
      {srcMode && (
        <textarea
          value={srcHtml}
          onChange={e => { setSrcHtml(e.target.value); onChange(e.target.value); }}
          spellCheck={false}
          style={{ width:"100%", minHeight:480, padding:"24px 32px", outline:"none", border:"none", resize:"vertical", fontFamily:"'Menlo','Monaco','Courier New',monospace", fontSize:12, lineHeight:1.7, color:"#334155", background:"#f8fafc", boxSizing:"border-box" }}
        />
      )}

      {/* Image insert / replace picker */}
      {showImgPicker && (
        <MediaPickerModal
          filter="image"
          title={pendingImg.current ? "Replace Image" : "Insert Image"}
          profile={profile}
          imageQuality={imageQuality}
          defaultTab={pendingImg.current ? "generate" : "library"}
          onSelect={(file) => {
            if (pendingImg.current) {
              // Replace existing image
              pendingImg.current.src = file.url;
              pendingImg.current = null;
            } else {
              // Insert new image at saved cursor position
              ref.current?.focus();
              const sel = window.getSelection();
              if (savedRange.current) {
                sel.removeAllRanges();
                sel.addRange(savedRange.current);
                savedRange.current = null;
              }
              document.execCommand("insertHTML", false, `<img src="${file.url}" style="max-width:100%;border-radius:6px;margin:8px 0;display:block;" />`);
            }
            onChange(ref.current.innerHTML);
            setShowImgPicker(false);
          }}
          onClose={() => { pendingImg.current = null; savedRange.current = null; setShowImgPicker(false); }}
        />
      )}
    </div>
  );
}

// ── GBPEditor — shared by GBP + Social ────────────────────────────────────────
export function GBPEditor({ value, onChange, onGenerate, loading, disabled = false, charLimit = 1500, placeholder = "Write your post here…" }) {
  const ref     = useRef(null);
  const lockRef = useRef(false); // prevent cursor jump on external value update
  const [showEmoji, setShowEmoji] = useState(false);
  const EMOJIS = ["🎉","✨","🍽️","⭐","🔥","💯","🙌","📣","🎊","🏆","❤️","👍","💪","🎁","🛎️","📍","🕐","📞","🌟","💬","🍀","🥳","😊","👏","🚀"];

  // Sync external value (e.g. from AI generation) without clobbering cursor
  useEffect(() => {
    if (!ref.current || lockRef.current) return;
    if (ref.current.innerText.trim() !== value.trim()) {
      ref.current.innerText = value;
      // Move cursor to end
      const range = document.createRange();
      const sel   = window.getSelection();
      range.selectNodeContents(ref.current);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [value]);

  const handleInput = () => {
    lockRef.current = true;
    onChange(ref.current?.innerText || "");
    setTimeout(() => { lockRef.current = false; }, 0);
  };

  const insertText = (txt) => {
    ref.current?.focus();
    document.execCommand("insertText", false, txt);
    setShowEmoji(false);
  };

  const insertLineBreak = () => {
    ref.current?.focus();
    document.execCommand("insertLineBreak", false);
  };

  const charCount = value.length;
  const charColor = charCount > charLimit ? C.danger : charCount > charLimit * 0.87 ? "#f59e0b" : C.t3;

  return (
    <div style={{ border:`1px solid ${C.border}`, borderRadius:10, overflow:"visible", background:C.s1 }}>
      {/* Toolbar */}
      <div style={{ display:"flex", alignItems:"center", gap:2, padding:"7px 10px", background:C.s2, borderBottom:`1px solid ${C.border}`, flexWrap:"wrap" }}>
        {/* AI button */}
        <div style={{ position:"relative", marginRight:6 }}>
          <button onClick={disabled ? undefined : onGenerate} disabled={loading || disabled}
            title={disabled ? "Add a topic above first, then click AI to generate" : "Generate post with AI"}
            style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 12px", borderRadius:6, border:"none",
              background: loading ? C.s4 : disabled ? C.s4 : "#7c3aed",
              color: disabled ? C.t3 : "#fff",
              fontSize:12, fontWeight:700,
              cursor: loading||disabled ? "not-allowed" : "pointer",
              fontFamily:"inherit", opacity: disabled ? 0.55 : 1, transition:"all 0.15s" }}>
            ✦ {loading ? "Writing…" : "AI"}
          </button>
          {disabled && (
            <div style={{ position:"absolute", top:"110%", left:0, zIndex:50, background:"#1c2437", color:"#fff",
              fontSize:11, padding:"6px 10px", borderRadius:6, whiteSpace:"nowrap", pointerEvents:"none",
              boxShadow:"0 4px 12px rgba(0,0,0,0.2)", display:"none" }}
              className="ai-tooltip">
              Add a topic above first
            </div>
          )}
        </div>
        <div style={{ width:1, height:18, background:C.border, margin:"0 3px", flexShrink:0 }} />
        {/* Bold / Italic — visual only, reminder: GBP strips formatting */}
        {[["B",{fontWeight:700},"Bold"],["I",{fontStyle:"italic"},"Italic"]].map(([lbl,s,title]) => (
          <button key={lbl} title={title} onMouseDown={e=>e.preventDefault()}
            onClick={() => { ref.current?.focus(); document.execCommand(lbl==="B"?"bold":"italic"); }}
            style={{ padding:"4px 9px", borderRadius:5, border:"1px solid transparent", background:"transparent",
              color:C.t2, fontSize:12, cursor:"pointer", fontFamily:"inherit", ...s, lineHeight:1.4, transition:"all 0.1s" }}
            onMouseOver={e=>{e.currentTarget.style.background=C.s3;e.currentTarget.style.borderColor=C.border;}}
            onMouseOut={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.borderColor="transparent";}}>
            {lbl}
          </button>
        ))}
        <div style={{ width:1, height:18, background:C.border, margin:"0 3px", flexShrink:0 }} />
        {/* Emoji picker */}
        <div style={{ position:"relative" }}>
          <button title="Emoji" onMouseDown={e=>e.preventDefault()} onClick={()=>setShowEmoji(v=>!v)}
            style={{ padding:"4px 9px", borderRadius:5, border:`1px solid ${showEmoji?C.border:"transparent"}`, background:showEmoji?C.s3:"transparent",
              color:C.t2, fontSize:14, cursor:"pointer", lineHeight:1.4 }}>
            😊
          </button>
          {showEmoji && (
            <div style={{ position:"absolute", top:"110%", left:0, zIndex:50, background:C.s1, border:`1px solid ${C.border}`, borderRadius:10, padding:10,
              display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:4, boxShadow:"0 8px 24px rgba(0,0,0,0.12)", width:220 }}>
              {EMOJIS.map(em => (
                <button key={em} onMouseDown={e=>e.preventDefault()} onClick={()=>insertText(em)}
                  style={{ fontSize:18, background:"none", border:"none", cursor:"pointer", padding:"4px", borderRadius:5, transition:"background 0.1s" }}
                  onMouseOver={e=>e.currentTarget.style.background=C.s3}
                  onMouseOut={e=>e.currentTarget.style.background="none"}>
                  {em}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Line break */}
        <button title="Insert line break" onMouseDown={e=>e.preventDefault()} onClick={insertLineBreak}
          style={{ padding:"4px 9px", borderRadius:5, border:"1px solid transparent", background:"transparent",
            color:C.t2, fontSize:12, cursor:"pointer", fontFamily:"inherit", lineHeight:1.4 }}
          onMouseOver={e=>{e.currentTarget.style.background=C.s3;e.currentTarget.style.borderColor=C.border;}}
          onMouseOut={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.borderColor="transparent";}}>
          ↵
        </button>
        {/* Char count pushed to right */}
        <span style={{ marginLeft:"auto", fontSize:12, fontWeight:600, color:charColor }}>{charCount}/{charLimit}</span>
      </div>
      {/* Editable area */}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onFocus={()=>setShowEmoji(false)}
        style={{ minHeight:180, padding:"14px 16px", outline:"none", lineHeight:1.7, fontSize:13,
          color:C.text, background:C.s1, overflowY:"auto", whiteSpace:"pre-wrap", wordBreak:"break-word" }}
      />
      {/* Placeholder */}
      {!value && (
        <div style={{ position:"absolute", pointerEvents:"none", fontSize:13, color:C.t4, padding:"14px 16px", lineHeight:1.7, marginTop:-`${180}px` }}>
        </div>
      )}
      {charCount > charLimit && (
        <div style={{ padding:"6px 12px", background:`${C.danger}10`, borderTop:`1px solid ${C.danger}30`, fontSize:11, color:C.danger }}>
          Post exceeds {charLimit} characters — platform may trim it.
        </div>
      )}
    </div>
  );
}

// ── Markdown renderer ─────────────────────────────────────────────────────────
export function MarkdownView({ text, style = {} }) {
  if (!text) return null;
  const lines   = text.split("\n");
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("### ")) {
      elements.push(<h3 key={i} style={{ fontSize:15, fontWeight:700, color:"#000000", margin:"20px 0 6px" }}>{line.slice(4)}</h3>);
    } else if (line.startsWith("## ")) {
      elements.push(<h2 key={i} style={{ fontSize:18, fontWeight:700, color:"#000000", margin:"28px 0 8px", borderBottom:`1px solid ${C.border}`, paddingBottom:6 }}>{line.slice(3)}</h2>);
    } else if (line.startsWith("# ")) {
      elements.push(<h1 key={i} style={{ fontSize:22, fontWeight:700, color:"#000000", margin:"0 0 16px", lineHeight:1.3 }}>{line.slice(2)}</h1>);
    } else if (/^!\[.*?\]\(/.test(line)) {
      const m = line.match(/^!\[([^\]]*)\]\(([^)]+)\)/);
      if (m) elements.push(<img key={i} src={m[2]} alt={m[1]} style={{ width:"100%", borderRadius:8, margin:"12px 0", display:"block", maxHeight:420, objectFit:"cover" }} />);
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      const bullets = [];
      while (i < lines.length && (lines[i].startsWith("- ") || lines[i].startsWith("* "))) {
        bullets.push(<li key={i} style={{ marginBottom:4 }} dangerouslySetInnerHTML={{ __html: parseInline(lines[i].slice(2)) }} />);
        i++;
      }
      elements.push(<ul key={`ul${i}`} style={{ paddingLeft:20, margin:"8px 0 12px", color:"#000000" }}>{bullets}</ul>);
      continue;
    } else if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(<li key={i} style={{ marginBottom:4 }} dangerouslySetInnerHTML={{ __html: parseInline(lines[i].replace(/^\d+\.\s/,"")) }} />);
        i++;
      }
      elements.push(<ol key={`ol${i}`} style={{ paddingLeft:20, margin:"8px 0 12px", color:"#000000" }}>{items}</ol>);
      continue;
    } else if (!line.trim()) {
      elements.push(<div key={i} style={{ height:10 }} />);
    } else {
      elements.push(<p key={i} style={{ margin:"0 0 10px", lineHeight:1.75, color:"#000000" }} dangerouslySetInnerHTML={{ __html: parseInline(line) }} />);
    }
    i++;
  }
  return <div style={{ fontSize:14, ...style }}>{elements}</div>;
}

export function LibraryCard({ item, onView, onDelete, onEdit }) {
  const col     = CW_TYPE_COL[item.type] || C.accent;
  const preview = item.content?.slice(0, 140).replace(/#+\s/g,"").replace(/\*\*/g,"") + "…";
  return (
    <div style={{ ...S.card, padding:16, display:"flex", gap:14, alignItems:"flex-start" }}>
      <div style={{ width:4, alignSelf:"stretch", borderRadius:2, background:col, flexShrink:0 }} />
      <div style={{ flex:1, minWidth:0, cursor:"pointer" }} onClick={onView}>
        <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:3 }}>{item.title}</div>
        <div style={{ fontSize:12, color:C.t2, lineHeight:1.5, marginBottom:4 }}>{preview}</div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          <span style={{ fontSize:10, color:C.t3 }}>{item.business} · {new Date(item.savedAt).toLocaleDateString(undefined,{month:"short",day:"numeric",year:"numeric"})}</span>
          {item.sourceTopicTitle && (
            <span style={{ fontSize:10, padding:"1px 7px", borderRadius:8, background:`${C.success}15`, color:C.success, fontWeight:600, display:"flex", alignItems:"center", gap:3 }}>
              💡 From topic
            </span>
          )}
          {item.keyword && (
            <span style={{ fontSize:10, padding:"1px 7px", borderRadius:8, background:`${C.accent}12`, color:C.accent, fontWeight:600 }}>
              🎯 {item.keyword}
            </span>
          )}
        </div>
      </div>
      <div style={{ display:"flex", gap:6, flexShrink:0 }}>
        <button onClick={onView}  style={{ ...S.btnGhost, padding:"5px 12px", fontSize:11 }}>View</button>
        <button onClick={onEdit}  style={{ ...S.btnGhost, padding:"5px 12px", fontSize:11 }}>Edit</button>
        <button onClick={onDelete} style={{ background:"none", border:"none", color:C.t4, cursor:"pointer", fontSize:16, padding:"0 4px" }} onMouseOver={e=>e.currentTarget.style.color=C.danger} onMouseOut={e=>e.currentTarget.style.color=C.t4}>✕</button>
      </div>
    </div>
  );
}

// ── GenerateImagePanel — sub-component of MediaPickerModal ───────────────────
// Also used standalone in the Creative Agent / Image Studio.
export function GenerateImagePanel({ profile, onSelect, defaultAspect = "landscape", imageQuality = "standard" }) {
  const isMobile = useIsMobile();
  const [prompt,    setPrompt]    = useState("");
  const [style,     setStyle]     = useState("photorealistic");
  const [aspect,    setAspect]    = useState(defaultAspect);
  const quality = imageQuality; // resolved from workspace config, not user-selectable
  const [imgUrl,    setImgUrl]    = useState(null);
  const [provider,  setProvider]  = useState(null);
  const [loading,   setLoading]   = useState(false); // server call in progress
  const [imgLoaded, setImgLoaded] = useState(false); // browser finished downloading image
  const [imgError,  setImgError]  = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [saved,     setSaved]     = useState(false);

  const biz  = profile?.name     || "our business";
  const cat  = profile?.category || "restaurant";
  const city = profile?.city     || "";

  const STYLES = [
    { id:"photorealistic", label:"Photo"        },
    { id:"illustration",   label:"Illustration" },
    { id:"minimalist",     label:"Minimal"      },
    { id:"bold",           label:"Bold"         },
  ];
  const ASPECTS = [
    { id:"square",    label:"Square",    hint:"1:1"  },
    { id:"landscape", label:"Landscape", hint:"16:9" },
    { id:"portrait",  label:"Portrait",  hint:"9:16" },
    { id:"banner",    label:"Banner",    hint:"3:1"  },
  ];
  const STYLE_SUFFIX = {
    photorealistic: "professional photography, ultra-realistic, commercial quality, sharp focus",
    illustration:   "digital illustration, artistic, vibrant colors, clean linework",
    minimalist:     "clean minimalist design, simple shapes, lots of white space, modern",
    bold:           "bold graphic design, high contrast, vivid colors, eye-catching composition",
  };

  const enhancePrompt = async () => {
    if (!prompt.trim()) return;
    setEnhancing(true);
    try {
      const r = await ai(
        `Improve this image generation prompt for a ${cat} called "${biz}"${city?` in ${city}`:""}. Make it vivid, specific, and visually descriptive. Under 100 words. Return only the improved prompt.\n\nOriginal: ${prompt}`,
        "You write precise, evocative image generation prompts."
      );
      setPrompt(r.trim());
    } catch {}
    setEnhancing(false);
  };

  const generate = async () => {
    if (!prompt.trim()) return;
    setLoading(true); setImgUrl(null); setImgLoaded(false); setImgError(false); setSaved(false);
    const full = `${prompt}, ${STYLE_SUFFIX[style]}`;
    try {
      const wid = await getWorkspaceId().catch(() => null);
      const r = await fetch("/api/generate-image", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ prompt: full, quality, aspectRatio: aspect, workspaceId: wid }),
      });
      const d = await r.json();
      if (d.url) { setImgUrl(d.url); setProvider(d.provider); }
      else setImgError(true);
    } catch { setImgError(true); }
    setLoading(false);
    // Note: for Pollinations.ai, loading=false here but image still downloading.
    // imgLoaded=false keeps the loading UI visible until browser finishes download.
  };

  const saveToLibrary = async () => {
    if (!imgUrl) return;
    const file = { id:Date.now(), name:`ai-${Date.now()}.jpg`, url:imgUrl, type:"image", source:"generated", prompt, provider, savedAt:new Date().toISOString() };
    const existing = await store.get("media-library") || [];
    await store.set("media-library", [file, ...existing]);
    setSaved(true);
  };

  const AR_STYLE = { square:"1/1", landscape:"16/9", portrait:"9/16", banner:"3/1" };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {/* Prompt */}
      <div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
          <label style={{ fontSize:12, fontWeight:600, color:C.t2 }}>Describe the image</label>
          <button onMouseDown={e=>e.preventDefault()} onClick={enhancePrompt} disabled={enhancing || !prompt.trim()}
            style={{ ...S.btnGhost, fontSize:11, padding:"3px 10px", color:C.accent, borderColor:`${C.accent}40` }}>
            {enhancing ? "Enhancing…" : "✦ Enhance with AI"}
          </button>
        </div>
        <textarea value={prompt} onChange={e=>setPrompt(e.target.value)}
          placeholder={`e.g. "A beautifully plated pasta dish, soft candlelight, rustic Italian restaurant"`}
          style={{ ...S.input, width:"100%", height:72, resize:"vertical", boxSizing:"border-box" }} />
        <div style={{ fontSize:11, color:C.t3, marginTop:3 }}>
          Context: {biz}{city?` · ${city}`:""}
        </div>
      </div>

      {/* Controls */}
      <div style={{ display:"grid", gridTemplateColumns: isMobile?"1fr":"1fr 1fr 1fr", gap:12 }}>
        <div>
          <div style={{ fontSize:11, fontWeight:600, color:C.t2, marginBottom:6 }}>Style</div>
          <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
            {STYLES.map(s => (
              <button key={s.id} onMouseDown={e=>e.preventDefault()} onClick={()=>setStyle(s.id)}
                style={{ padding:"3px 10px", borderRadius:20, fontSize:11, cursor:"pointer", fontFamily:"inherit",
                  border:`1px solid ${style===s.id?C.accent:C.border}`, background:style===s.id?`${C.accent}12`:"transparent",
                  color:style===s.id?C.accent:C.t2, fontWeight:style===s.id?600:400 }}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize:11, fontWeight:600, color:C.t2, marginBottom:6 }}>Aspect Ratio</div>
          <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
            {ASPECTS.map(a => (
              <button key={a.id} onMouseDown={e=>e.preventDefault()} onClick={()=>setAspect(a.id)}
                style={{ padding:"3px 10px", borderRadius:20, fontSize:11, cursor:"pointer", fontFamily:"inherit",
                  border:`1px solid ${aspect===a.id?C.accent:C.border}`, background:aspect===a.id?`${C.accent}12`:"transparent",
                  color:aspect===a.id?C.accent:C.t2, fontWeight:aspect===a.id?600:400 }}>
                {a.label} <span style={{ opacity:0.55, fontSize:10 }}>{a.hint}</span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize:11, fontWeight:600, color:C.t2, marginBottom:6 }}>Engine</div>
          <div style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"4px 12px", borderRadius:20, fontSize:11, fontWeight:600,
            border:`1px solid ${IMAGE_PROVIDERS.find(p=>p.id===quality)?.color||C.border}40`,
            background:`${IMAGE_PROVIDERS.find(p=>p.id===quality)?.color||C.accent}10`,
            color:IMAGE_PROVIDERS.find(p=>p.id===quality)?.color||C.t2 }}>
            ✦ {IMAGE_PROVIDER_LABEL[quality] || quality}
          </div>
          <div style={{ fontSize:10, color:C.t4, marginTop:4 }}>Configured by your plan</div>
        </div>
      </div>

      <button onMouseDown={e=>e.preventDefault()} style={{ ...S.btn, alignSelf:"flex-start" }}
        disabled={loading || !prompt.trim()} onClick={generate}>
        {loading ? "✦ Generating…" : "✦ Generate Image"}
      </button>

      {/* Preview */}
      {(loading || imgUrl) && (
        <div style={{ borderRadius:12, overflow:"hidden", background:C.s3, position:"relative", aspectRatio: AR_STYLE[aspect] }}>
          {/* Keep loading UI visible until browser finishes downloading the image */}
          {(loading || (imgUrl && !imgLoaded && !imgError)) && (
            <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:10 }}>
              <div style={{ fontSize:32, animation:"spin 1.5s linear infinite", color:C.accent }}>✦</div>
              <div style={{ fontSize:13, color:C.t2, fontWeight:600 }}>{quality==="hd"?"DALL-E 3 generating…":quality==="imagen"?"Imagen generating…":"Flux generating…"}</div>
              <div style={{ fontSize:11, color:C.t3 }}>This takes 10–20 seconds</div>
            </div>
          )}
          {imgError && (
            <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <div style={{ fontSize:13, color:C.danger }}>Generation failed — try again</div>
            </div>
          )}
          {imgUrl && (
            <>
              <img src={imgUrl} alt="Generated"
                onLoad={()=>setImgLoaded(true)}
                onError={()=>{ setImgError(true); setImgLoaded(false); }}
                style={{ width:"100%", height:"100%", objectFit:"cover", display:imgLoaded?"block":"none" }} />
              {imgLoaded && (
                <div style={{ position:"absolute", bottom:8, right:8, fontSize:10, background:"rgba(0,0,0,0.6)", color:"rgba(255,255,255,0.9)", padding:"3px 8px", borderRadius:5 }}>
                  ✦ {provider==="dall-e-3"?"DALL-E 3":provider==="imagen"?"Imagen 3":"Flux"}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Actions */}
      {imgUrl && imgLoaded && (
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <button style={{ ...S.btn }} onMouseDown={e=>e.preventDefault()} onClick={()=>onSelect(imgUrl)}>
            ✓ Use This Image
          </button>
          <button style={{ ...S.btnGhost }} onMouseDown={e=>e.preventDefault()} onClick={generate}>
            ↺ Regenerate
          </button>
          <button style={{ ...S.btnGhost, color: saved?C.success:C.t2 }}
            onMouseDown={e=>e.preventDefault()} onClick={saveToLibrary} disabled={saved}>
            {saved ? "✓ Saved" : "⊞ Save to Library"}
          </button>
        </div>
      )}
    </div>
  );
}

export function MediaPickerModal({ onSelect, onClose, filter = "image", title = "Pick Image", profile, defaultTab = "library", imageQuality = "standard" }) {
  const isMobile = useIsMobile();
  const [pickerTab, setPickerTab] = useState(defaultTab);
  const [files, setFiles]   = useState([]);
  const [search, setSearch] = useState("");
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    store.get("media-library").then(d => {
      const all = d || [];
      setFiles(filter === "all" ? all : all.filter(f => f.type === filter));
    });
  }, [filter]);

  const shown = files.filter(f => !search || f.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:2000,
      display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:C.s1, borderRadius:16, width:"min(760px,100%)", maxHeight:"90vh",
        display:"flex", flexDirection:"column", boxShadow:"0 24px 64px rgba(0,0,0,0.25)" }}>

        {/* Header */}
        <div style={{ padding:"16px 24px", borderBottom:`1px solid ${C.border}`,
          display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
          {/* Tabs */}
          <div style={{ display:"flex", gap:4 }}>
            {[{id:"library",label:"⊞ Library"},{id:"generate",label:"✦ Generate"}].map(t => (
              <button key={t.id} onMouseDown={e=>e.preventDefault()} onClick={()=>setPickerTab(t.id)}
                style={{ padding:"6px 14px", borderRadius:8, fontSize:13, fontWeight:pickerTab===t.id?700:500,
                  background:pickerTab===t.id?C.s3:"transparent", color:pickerTab===t.id?C.text:C.t3,
                  border:`1px solid ${pickerTab===t.id?C.border:"transparent"}`, cursor:"pointer", fontFamily:"inherit" }}>
                {t.label}
              </button>
            ))}
          </div>
          <button onMouseDown={e=>e.preventDefault()} onClick={onClose}
            style={{ background:"none", border:"none", fontSize:20, color:C.t3, cursor:"pointer", lineHeight:1 }}>✕</button>
        </div>

        {/* Library tab */}
        {pickerTab === "library" && <>
          <div style={{ padding:"12px 20px", borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search files…" autoFocus
              style={{ ...S.input, width:"100%", fontSize:13 }} />
          </div>
          <div style={{ flex:1, overflowY:"auto", padding:20 }}>
            {files.length === 0 ? (
              <div style={{ textAlign:"center", padding:"48px 24px" }}>
                <div style={{ fontSize:40, marginBottom:12, color:C.t4 }}>⊞</div>
                <div style={{ fontSize:14, fontWeight:600, color:C.text, marginBottom:8 }}>No media files yet</div>
                <div style={{ fontSize:13, color:C.t3, marginBottom:16 }}>Upload images in Media Library, or generate one with AI.</div>
                <button onMouseDown={e=>e.preventDefault()} onClick={()=>setPickerTab("generate")}
                  style={{ ...S.btn }}>✦ Generate with AI →</button>
              </div>
            ) : shown.length === 0 ? (
              <div style={{ textAlign:"center", padding:"32px", color:C.t3, fontSize:13 }}>No files match your search.</div>
            ) : (
              <div style={{ display:"grid", gridTemplateColumns:`repeat(auto-fill,minmax(${isMobile?100:140}px,1fr))`, gap:10 }}>
                {shown.map(file => (
                  <div key={file.id}
                    onClick={() => onSelect(file)}
                    onMouseEnter={() => setHovered(file.id)}
                    onMouseLeave={() => setHovered(null)}
                    style={{ borderRadius:10, overflow:"hidden", cursor:"pointer",
                      border:`2px solid ${hovered===file.id?C.accent:C.border}`,
                      background:C.s1, transform: hovered===file.id?"scale(1.02)":"scale(1)", transition:"all 0.12s" }}>
                    <div style={{ position:"relative", paddingBottom:"75%", background:C.s3, overflow:"hidden" }}>
                      <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                        {file.type === "image"
                          ? <img src={file.url} alt={file.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                          : <div style={{ fontSize:32, color:C.t4 }}>⊞</div>}
                      </div>
                    </div>
                    <div style={{ padding:"6px 8px" }}>
                      <div style={{ fontSize:11, fontWeight:500, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{file.name}</div>
                      {file.width && <div style={{ fontSize:10, color:C.t3 }}>{file.width}×{file.height}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ padding:"10px 20px", borderTop:`1px solid ${C.border}`, flexShrink:0,
            fontSize:12, color:C.t3, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span>{shown.length} file{shown.length !== 1 ? "s" : ""}</span>
            <button onMouseDown={e=>e.preventDefault()} onClick={onClose} style={{ ...S.btnGhost, padding:"5px 14px", fontSize:12 }}>Cancel</button>
          </div>
        </>}

        {/* Generate tab */}
        {pickerTab === "generate" && (
          <div style={{ flex:1, overflowY:"auto", padding:24 }}>
            <GenerateImagePanel
              profile={profile}
              defaultAspect="landscape"
              imageQuality={imageQuality}
              onSelect={url => {
                onSelect({ id:Date.now(), url, name:`ai-${Date.now()}.jpg`, type:"image", source:"generated" });
                onClose();
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // H1
    if (/^#\s+/.test(line)) {
      elements.push(<div key={i} style={{ fontWeight:700, fontSize:14, color:"inherit", marginBottom:4, marginTop: elements.length?10:0 }}>{line.replace(/^#\s+/,"")}</div>);
    // H2
    } else if (/^##\s+/.test(line)) {
      elements.push(<div key={i} style={{ fontWeight:700, fontSize:13, color:"inherit", marginBottom:3, marginTop: elements.length?8:0 }}>{line.replace(/^##\s+/,"")}</div>);
    // H3
    } else if (/^###\s+/.test(line)) {
      elements.push(<div key={i} style={{ fontWeight:600, fontSize:12, color:"inherit", marginBottom:2, marginTop: elements.length?6:0 }}>{line.replace(/^###\s+/,"")}</div>);
    // Numbered list
    } else if (/^\d+\.\s+/.test(line)) {
      const num = line.match(/^(\d+)\.\s+/)[1];
      const content = line.replace(/^\d+\.\s+/,"");
      elements.push(
        <div key={i} style={{ display:"flex", gap:6, marginBottom:3 }}>
          <span style={{ flexShrink:0, fontWeight:600, color:"inherit", opacity:0.7 }}>{num}.</span>
          <span>{inlineMarkdown(content)}</span>
        </div>
      );
    // Bullet list
    } else if (/^[-*•]\s+/.test(line)) {
      const content = line.replace(/^[-*•]\s+/,"");
      elements.push(
        <div key={i} style={{ display:"flex", gap:6, marginBottom:3 }}>
          <span style={{ flexShrink:0, color:C.accent }}>•</span>
          <span>{inlineMarkdown(content)}</span>
        </div>
      );
    // Blank line
    } else if (line.trim() === "") {
      if (elements.length > 0) elements.push(<div key={i} style={{ height:6 }} />);
    // Regular paragraph
    } else {
      elements.push(<div key={i} style={{ marginBottom:2 }}>{inlineMarkdown(line)}</div>);
    }
    i++;
  }
  return <>{elements}</>;
}

// ── ImagePackageLaunchModal — extracted from App.jsx ─────────────────────────
export function ImagePackageLaunchModal({ open, sourceType, sourceId, sourceModule, title, body, onClose }) {
  const [titleEdit, setTitleEdit] = useState(title || "");
  const [bodyEdit,  setBodyEdit]  = useState(body  || "");
  const [logoMode,  setLogoMode]  = useState("category_default");
  const [busy,      setBusy]      = useState(false);

  useEffect(() => { setTitleEdit(title || ""); setBodyEdit(body || ""); }, [title, body, open]);

  if (!open) return null;
  async function launch() {
    setBusy(true);
    try {
      const r = await fetch("/api/image-packages/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          workspaceId: window.__activeWorkspaceId,
          source_type: sourceType,
          source_id: sourceId,
          source_module: sourceModule,
          title: titleEdit,
          body: bodyEdit,
          options: { logo_mode: logoMode },
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        alert(j.error || "Failed to start image package generation");
        setBusy(false);
        return;
      }
      onClose();
      window.dispatchEvent(new CustomEvent("navigate-to", { detail: { mod: "image-studio", from: sourceModule, label: title || sourceModule } }));
    } catch (e) { console.error(e); setBusy(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: C.s1, borderRadius: 10, padding: 24, maxWidth: 520, width: "94vw" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 12px", color: C.text }}>✦ Generate Image Package</h3>
        <Field label="Title">
          <input type="text" value={titleEdit} onChange={(e) => setTitleEdit(e.target.value)} style={S.input} />
        </Field>
        <Field label="Description (optional)">
          <textarea value={bodyEdit} onChange={(e) => setBodyEdit(e.target.value)} rows={3} style={{ ...S.input, resize: "vertical" }} />
        </Field>
        <Field label="Logo mode">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <Chip label="Default (smart)" active={logoMode === "category_default"} onClick={() => setLogoMode("category_default")} />
            <Chip label="All"             active={logoMode === "all"}              onClick={() => setLogoMode("all")} />
            <Chip label="None"            active={logoMode === "none"}             onClick={() => setLogoMode("none")} />
          </div>
        </Field>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onClose} style={S.btnGhost}>Cancel</button>
          <button onClick={launch} disabled={busy || !titleEdit.trim()} style={{ ...S.btn, opacity: (busy || !titleEdit.trim()) ? 0.6 : 1 }}>{busy ? "⠙ Starting…" : "✦ Generate"}</button>
        </div>
      </div>
    </div>
  );
}

// ── InlineField — click-to-edit field (used by CRM, Conversion, settings) ─────
export function InlineField({ label, value, onSave, type = "text", options, readOnly = false, placeholder = "Click to add…" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  useEffect(() => { setVal(value || ""); }, [value]);
  const commit = () => { setEditing(false); if (val !== (value || "") && onSave) onSave(val); };
  if (readOnly) return (
    <div style={{ marginBottom:12 }}>
      <div style={{ fontSize:11, color:C.t3, fontWeight:600, textTransform:"uppercase",
        letterSpacing:"0.06em", marginBottom:3 }}>{label}</div>
      <div style={{ fontSize:13, color:C.t2 }}>{value || "—"}</div>
    </div>
  );
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ fontSize:11, color:C.t3, fontWeight:600, textTransform:"uppercase",
        letterSpacing:"0.06em", marginBottom:3 }}>{label}</div>
      {editing ? (
        options ? (
          <select value={val} onChange={e => setVal(e.target.value)} onBlur={commit} autoFocus
            style={{ ...S.input, fontSize:13, padding:"4px 8px" }}>
            {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ) : (
          <input value={val} onChange={e => setVal(e.target.value)} onBlur={commit}
            onKeyDown={e => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") { setVal(value || ""); setEditing(false); }
            }} autoFocus type={type}
            style={{ ...S.input, fontSize:13, padding:"4px 8px" }} />
        )
      ) : (
        <div onClick={() => setEditing(true)} title="Click to edit"
          style={{ fontSize:13, color: value ? C.text : C.t4, cursor:"pointer",
            padding:"4px 6px", borderRadius:4, border:`1px dashed ${C.border}`,
            transition:"border-color 0.15s, background 0.15s",
            display:"flex", alignItems:"center", gap:6 }}
          onMouseOver={e => { e.currentTarget.style.borderColor = "#6366f1"; e.currentTarget.style.background = "#6366f108"; }}
          onMouseOut={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = "transparent"; }}>
          <span style={{ flex:1 }}>
            {value
              ? (options ? (options.find(([v]) => v === value)?.[1] ?? value) : value)
              : <span style={{ color:C.t4 }}>{placeholder}</span>}
          </span>
          <span style={{ fontSize:11, color:"#6366f1" }}>✎</span>
        </div>
      )}
    </div>
  );
}

/* ─── CALL UI ────────────────────────────────────────────────────────── */
// ── TranscriptSection — collapsible transcript (used by CallPanel + CRM) ──────
export function TranscriptSection({ transcript }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border:`1px solid ${C.border}`, borderRadius:8, overflow:"hidden" }}>
      <button onMouseDown={e => e.preventDefault()} onClick={() => setOpen(v => !v)}
        style={{ width:"100%", padding:"8px 12px", background:C.s2, border:"none",
          cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between",
          fontSize:12, color:C.t2, fontFamily:"inherit" }}>
        <span>Full Transcript</span>
        <span style={{ fontSize:10, color:C.t4 }}>{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div style={{ padding:"10px 12px", maxHeight:200, overflowY:"auto" }}>
          {transcript
            ? <div style={{ fontSize:12, color:C.t2, lineHeight:1.7, whiteSpace:"pre-wrap" }}>{transcript}</div>
            : <div style={{ fontSize:12, color:C.t3, fontStyle:"italic" }}>Transcript processing…</div>
          }
        </div>
      )}
    </div>
  );
}

// ── CallPanel — WebRTC call UI (used by CRM + Conversion) ─────────────────────
export function CallPanel({ callState, dialNumber, setDialNumber, onCall, onCancel, onEnd,
  isMuted, onMute, isOnHold, onHold, isSpeaker, onSpeaker,
  duration, notes, setNotes, showKeypad, setShowKeypad, onDigit,
  outcome, setOutcome, summary, transcript, onSave, contactName }) {

  const fmt = s => `${Math.floor(s/60)}:${(s%60).toString().padStart(2,"0")}`;
  const KEYS = [["1",""],["2","ABC"],["3","DEF"],["4","GHI"],["5","JKL"],["6","MNO"],
                ["7","PQRS"],["8","TUV"],["9","WXYZ"],["*",""],["0","+"],["#",""]];
  const KeyPad = ({ onPress }) => (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6 }}>
      {KEYS.map(([digit, letters]) => (
        <button key={digit} onClick={() => onPress(digit)}
          style={{ background:C.s2, border:`1px solid ${C.border}`, borderRadius:8,
            padding:"10px 0", cursor:"pointer", display:"flex", flexDirection:"column",
            alignItems:"center", gap:1, fontFamily:"inherit" }}>
          <span style={{ fontSize:16, fontWeight:600, color:C.text }}>{digit}</span>
          <span style={{ fontSize:9, color:C.t4 }}>{letters||" "}</span>
        </button>
      ))}
    </div>
  );

  // ── State 1: Dial Pad ─────────────────────────────────────────────
  if (callState === "dialpad") return (
    <div style={{ flex:1, overflowY:"auto", padding:"16px 20px",
      display:"flex", flexDirection:"column", gap:12 }}>
      <div style={{ background:C.s2, border:`1px solid ${C.border}`, borderRadius:10,
        padding:"10px 14px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:11, color:C.t3, marginBottom:2 }}>Calling</div>
          <input value={dialNumber} onChange={e => setDialNumber(e.target.value)}
            style={{ border:"none", background:"transparent", fontSize:16, fontWeight:700,
              color:C.text, outline:"none", width:"100%", fontVariantNumeric:"tabular-nums",
              fontFamily:"inherit" }}
            placeholder="+1 (___) ___-____" />
        </div>
        {dialNumber && (
          <button onMouseDown={e => e.preventDefault()} onClick={() => setDialNumber(d => d.slice(0,-1))}
            style={{ background:"none", border:"none", fontSize:18, color:C.t3,
              cursor:"pointer", padding:4, flexShrink:0 }}>⌫</button>
        )}
      </div>
      <KeyPad onPress={d => setDialNumber(n => n + d)} />
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"0 8px", marginTop:4 }}>
        <button onClick={onCancel}
          style={{ background:"none", border:"none", fontSize:12, color:C.t3, cursor:"pointer" }}>
          ✕ Close
        </button>
        <button onClick={onCall} disabled={!dialNumber.trim()}
          style={{ width:56, height:56, borderRadius:"50%",
            background: dialNumber.trim() ? C.success : C.s4, border:"none",
            cursor: dialNumber.trim() ? "pointer" : "not-allowed",
            boxShadow: dialNumber.trim() ? `0 4px 18px ${C.success}60` : "none",
            display:"flex", alignItems:"center", justifyContent:"center",
            transition:"all 0.2s" }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.81a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.03z"/>
          </svg>
        </button>
        <div style={{ width:48 }} />
      </div>
    </div>
  );

  // ── State 2: Dialing ──────────────────────────────────────────────
  if (callState === "dialing") return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center",
      justifyContent:"center", padding:24, gap:16 }}>
      <div style={{ width:64, height:64, borderRadius:"50%", background:"#6366f115",
        border:"2px solid #6366f130", display:"flex", alignItems:"center",
        justifyContent:"center", position:"relative" }}>
        <div style={{ position:"absolute", inset:-6, borderRadius:"50%",
          border:"2px solid #6366f125",
          animation:"crm-pulse 1.4s ease-in-out infinite" }} />
        <span style={{ fontSize:24 }}>📞</span>
      </div>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{contactName || dialNumber}</div>
        <div style={{ fontSize:12, color:C.t3 }}>{dialNumber}</div>
        <div style={{ fontSize:12, color:"#6366f1", fontWeight:600, marginTop:6 }}>Calling…</div>
      </div>
      <button onClick={onEnd}
        style={{ width:52, height:52, borderRadius:"50%", background:C.danger, border:"none",
          cursor:"pointer", fontSize:20, color:"#fff", boxShadow:`0 4px 16px ${C.danger}50`,
          display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
      <span style={{ fontSize:11, color:C.t4 }}>Tap to cancel</span>
    </div>
  );

  // ── State 3: Active Call ──────────────────────────────────────────
  if (callState === "active") return (
    <div style={{ flex:1, overflowY:"auto", padding:"16px 20px",
      display:"flex", flexDirection:"column", gap:12 }}>
      <div style={{ background:"#dcfce7", borderRadius:8, padding:"8px 12px",
        display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <div style={{ width:7, height:7, borderRadius:"50%", background:C.success }} />
          <span style={{ fontSize:11, fontWeight:700, color:C.success }}>CONNECTED</span>
        </div>
        <span style={{ fontSize:16, fontWeight:700, color:C.text,
          fontVariantNumeric:"tabular-nums" }}>{fmt(duration)}</span>
      </div>
      {showKeypad && <KeyPad onPress={onDigit} />}
      <textarea value={notes} onChange={e => setNotes(e.target.value)}
        placeholder="Take call notes…"
        style={{ ...S.input, flex:1, minHeight:80, resize:"none", fontSize:13,
          lineHeight:1.5, fontFamily:"inherit" }} />
      <div style={{ display:"flex", justifyContent:"space-around", alignItems:"center" }}>
        {[
          { label:"Mute",    icon: isMuted    ? "🔇":"🎙",  active:isMuted,     action:onMute },
          { label:"Hold",    icon:"⏸",                      active:isOnHold,    action:onHold },
          { label:"Speaker", icon:"🔊",                      active:isSpeaker,   action:onSpeaker },
          { label:"Keypad",  icon:"⌨️",                     active:showKeypad,  action:()=>setShowKeypad(v=>!v) },
        ].map(({ label, icon, active, action }) => (
          <div key={label} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
            <button onClick={action}
              style={{ width:36, height:36, borderRadius:"50%", cursor:"pointer", fontSize:16,
                background: active ? "#6366f115" : C.s2,
                border: `1px solid ${active ? "#6366f140" : C.border}`,
                display:"flex", alignItems:"center", justifyContent:"center" }}>{icon}</button>
            <span style={{ fontSize:9, color: active ? "#6366f1" : C.t3 }}>{label}</span>
          </div>
        ))}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
          <button onClick={onEnd}
            style={{ width:44, height:44, borderRadius:"50%", background:C.danger, border:"none",
              cursor:"pointer", fontSize:18, color:"#fff", boxShadow:`0 4px 12px ${C.danger}50`,
              display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
          <span style={{ fontSize:9, color:C.danger, fontWeight:700 }}>End</span>
        </div>
      </div>
    </div>
  );

  // ── State 4: Post-Call ────────────────────────────────────────────
  if (callState === "postcall") return (
    <div style={{ flex:1, overflowY:"auto", padding:"16px 20px",
      display:"flex", flexDirection:"column", gap:12 }}>
      <div style={{ fontSize:13, fontWeight:700, color:C.text }}>
        Call ended · {fmt(duration)}
      </div>
      <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8,
        padding:"10px 12px" }}>
        <div style={{ fontSize:11, fontWeight:700, color:C.success, marginBottom:4 }}>✦ AI Summary</div>
        {summary
          ? <div style={{ fontSize:12, color:"#374151", lineHeight:1.6 }}>{summary}</div>
          : <div style={{ fontSize:12, color:C.t3, fontStyle:"italic" }}>Generating summary…</div>
        }
      </div>
      <div>
        <div style={{ fontSize:11, color:C.t3, marginBottom:6, fontWeight:600,
          textTransform:"uppercase", letterSpacing:"0.06em" }}>OUTCOME</div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {[["connected","Connected"],["voicemail","Left voicemail"],
            ["no_answer","No answer"],["wrong_number","Wrong number"]].map(([val,label]) => (
            <button key={val} onClick={() => setOutcome(val)}
              style={{ ...S.btnGhost, fontSize:11, padding:"4px 10px",
                background: outcome===val ? "#6366f115" : "transparent",
                borderColor: outcome===val ? "#6366f140" : C.border,
                color: outcome===val ? "#6366f1" : C.t2,
                fontWeight: outcome===val ? 700 : 400 }}>{label}</button>
          ))}
        </div>
      </div>
      <div>
        <div style={{ fontSize:11, color:C.t3, marginBottom:6, fontWeight:600,
          textTransform:"uppercase", letterSpacing:"0.06em" }}>NOTES</div>
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          style={{ ...S.input, width:"100%", minHeight:60, resize:"none",
            fontSize:12, lineHeight:1.5, fontFamily:"inherit" }} />
      </div>
      <TranscriptSection transcript={transcript} />
      <button onClick={onSave} style={{ ...S.btn, width:"100%", fontSize:13 }}>
        Save to Activity Log
      </button>
    </div>
  );

  return null;
}

// ── CTAAddChip — sub-component of BrandKitForm ───────────────────────────────
export function CTAAddChip({ onAdd }) {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState("");
  if (!editing) {
    return (
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setEditing(true)}
        style={{ ...S.btnGhost, fontSize: 12, padding: "4px 10px", borderRadius: 16 }}
      >⊕ Add</button>
    );
  }
  return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      <input
        type="text"
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && value.trim()) { onAdd(value.trim()); setValue(""); setEditing(false); }
          if (e.key === "Escape") { setValue(""); setEditing(false); }
        }}
        placeholder="Type a CTA…"
        style={{ ...S.input, height: 26, padding: "0 8px", fontSize: 12, width: 140 }}
      />
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => { if (value.trim()) { onAdd(value.trim()); setValue(""); } setEditing(false); }}
        style={{ ...S.btn, fontSize: 11, padding: "3px 8px" }}
      >Add</button>
    </span>
  );
}

// ── BrandKitForm — Brand Kit editor (used by Business Brain + Plan Dashboard) ─
export function BrandKitForm({ brandKit, onChange, onLogoReplace, workspaceId, wsMeta, profile }) {
  const isMobile = useIsMobile();
  const [mediaPickerOpen, setMediaPickerOpen] = React.useState(null); // "logo" | "icon" | null

  const updateColor = (key, value) => onChange({ colors: { ...(brandKit.colors || {}), [key]: value } });
  const updateFont  = (key, value) => onChange({ fonts:  { ...(brandKit.fonts  || {}), [key]: value } });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

      {/* ── Visual Identity ── */}
      <div>
        <h4 style={{ margin: "0 0 8px", fontSize: 13, color: C.t2, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Visual Identity</h4>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          <Field label="Primary color">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="color" value={brandKit.colors?.primary || "#c4824a"} onChange={(e) => updateColor("primary", e.target.value)} style={{ width: 36, height: 36, border: `1px solid ${C.border}`, borderRadius: 6, padding: 0, cursor: "pointer" }} />
              <input type="text" value={brandKit.colors?.primary || ""} placeholder="#c4824a" onChange={(e) => updateColor("primary", e.target.value)} style={{ ...S.input, fontFamily: "monospace" }} />
            </div>
          </Field>
          <Field label="Accent color">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="color" value={brandKit.colors?.accent || "#1c2437"} onChange={(e) => updateColor("accent", e.target.value)} style={{ width: 36, height: 36, border: `1px solid ${C.border}`, borderRadius: 6, padding: 0, cursor: "pointer" }} />
              <input type="text" value={brandKit.colors?.accent || ""} placeholder="#1c2437" onChange={(e) => updateColor("accent", e.target.value)} style={{ ...S.input, fontFamily: "monospace" }} />
            </div>
          </Field>
          <Field label="Logo">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {wsMeta?.logo_url ? <img src={wsMeta.logo_url} alt="" style={{ width: 56, height: 56, objectFit: "contain", border: `1px solid ${C.border}`, borderRadius: 6, background: C.s2 }} /> : <div style={{ width: 56, height: 56, border: `1px dashed ${C.border}`, borderRadius: 6, color: C.t4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>?</div>}
              <button onMouseDown={(e) => e.preventDefault()} onClick={() => setMediaPickerOpen("logo")} style={{ ...S.btnGhost, fontSize: 12 }}>⊕ Replace</button>
            </div>
          </Field>
          <Field label="Icon (optional)">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {brandKit.icon_url ? <img src={brandKit.icon_url} alt="" style={{ width: 56, height: 56, objectFit: "contain", border: `1px solid ${C.border}`, borderRadius: 6, background: C.s2 }} /> : <div style={{ width: 56, height: 56, border: `1px dashed ${C.border}`, borderRadius: 6, color: C.t4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>?</div>}
              <button onMouseDown={(e) => e.preventDefault()} onClick={() => setMediaPickerOpen("icon")} style={{ ...S.btnGhost, fontSize: 12 }}>⊕ Replace</button>
            </div>
          </Field>
          <Field label="Heading font">
            <select value={brandKit.fonts?.heading || "Inter"} onChange={(e) => updateFont("heading", e.target.value)} style={S.input}>
              {BRAND_FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Body font">
            <select value={brandKit.fonts?.body || "Inter"} onChange={(e) => updateFont("body", e.target.value)} style={S.input}>
              {BRAND_FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
        </div>
      </div>

      {/* ── Voice & Tone ── */}
      <div>
        <h4 style={{ margin: "0 0 8px", fontSize: 13, color: C.t2, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Voice & Tone</h4>
        <Field label="Tone">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {BRAND_VOICE_TONES.map((t) => (
              <Chip
                key={t.id}
                label={t.label}
                active={brandKit.voice?.tone === t.id}
                onClick={() => onChange({ voice: { ...(brandKit.voice || {}), tone: t.id } })}
              />
            ))}
          </div>
        </Field>
        <Field label={`Notes (${(brandKit.voice?.custom_note || "").length}/120)`}>
          <textarea
            value={brandKit.voice?.custom_note || ""}
            onChange={(e) => onChange({ voice: { ...(brandKit.voice || {}), custom_note: e.target.value.slice(0, 120) } })}
            placeholder="e.g. Approachable, never clinical"
            rows={2}
            style={{ ...S.input, resize: "vertical", lineHeight: 1.4 }}
          />
        </Field>
      </div>

      {/* ── Style & Audience ── */}
      <div>
        <h4 style={{ margin: "0 0 8px", fontSize: 13, color: C.t2, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Style & Audience</h4>
        <Field label="Visual style">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {BRAND_VISUAL_STYLES.map((s) => (
              <Chip key={s.id} label={s.label} active={brandKit.visual_style === s.id} onClick={() => onChange({ visual_style: s.id })} />
            ))}
          </div>
        </Field>
        <Field label={`Tagline (${(brandKit.tagline || "").length}/80)`}>
          <input
            type="text"
            value={brandKit.tagline || ""}
            onChange={(e) => onChange({ tagline: e.target.value.slice(0, 80) })}
            placeholder="e.g. Personalized primary care for you and your family"
            style={S.input}
          />
        </Field>
        <Field label={`Audience persona (${(brandKit.audience_persona || "").length}/300)`}>
          <textarea
            value={brandKit.audience_persona || ""}
            onChange={(e) => onChange({ audience_persona: e.target.value.slice(0, 300) })}
            placeholder="e.g. Busy parents who want a primary care doctor they can text, not just visit."
            rows={3}
            style={{ ...S.input, resize: "vertical", lineHeight: 1.4 }}
          />
        </Field>
      </div>

      {/* ── Preferred CTAs ── */}
      <div>
        <h4 style={{ margin: "0 0 8px", fontSize: 13, color: C.t2, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Preferred CTAs (1–3)</h4>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {(brandKit.cta_examples || []).map((cta, i) => (
            <span key={i} style={{ background: C.s2, border: `1px solid ${C.border}`, padding: "4px 8px 4px 10px", borderRadius: 16, fontSize: 12, color: C.text, display: "inline-flex", alignItems: "center", gap: 6 }}>
              {cta}
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const next = [...(brandKit.cta_examples || [])];
                  next.splice(i, 1);
                  onChange({ cta_examples: next });
                }}
                style={{ background: "transparent", border: "none", color: C.t3, cursor: "pointer", fontSize: 14, padding: 0 }}
              >⊗</button>
            </span>
          ))}
          {(brandKit.cta_examples || []).length < 3 && (
            <CTAAddChip onAdd={(text) => onChange({ cta_examples: [...(brandKit.cta_examples || []), text.slice(0, 30)] })} />
          )}
        </div>
        <div style={{ fontSize: 11, color: C.t3, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
          <span>Suggestions:</span>
          {(BRAND_CTA_SUGGESTIONS[wsMeta?.niche] || BRAND_CTA_SUGGESTIONS.default).map((s) => {
            const already = (brandKit.cta_examples || []).includes(s);
            const atCap   = (brandKit.cta_examples || []).length >= 3;
            return (
              <button
                key={s}
                onMouseDown={(e) => e.preventDefault()}
                disabled={already || atCap}
                onClick={() => onChange({ cta_examples: [...(brandKit.cta_examples || []), s] })}
                style={{ ...S.btnGhost, fontSize: 11, padding: "3px 8px", opacity: already || atCap ? 0.4 : 1 }}
              >+ {s}</button>
            );
          })}
        </div>
      </div>

      {/* ── Read-only (edit at source) ── */}
      <div style={{ borderTop: `1px solid ${C.s3}`, paddingTop: 12, fontSize: 11, color: C.t3, display: "flex", flexWrap: "wrap", gap: 12 }}>
        <span><strong style={{ color: C.t2 }}>Business name:</strong> {wsMeta?.business_name || "—"}</span>
        <span><strong style={{ color: C.t2 }}>Website:</strong> {wsMeta?.website || "—"}</span>
        <span><strong style={{ color: C.t2 }}>Industry:</strong> {wsMeta?.industry || wsMeta?.niche || "—"}</span>
      </div>

      {mediaPickerOpen && (
        <MediaPickerModal
          onSelect={(file) => {
            const url = typeof file === "string" ? file : file?.url;
            if (!url) return;
            if (mediaPickerOpen === "logo") onLogoReplace?.(url);
            else onChange({ icon_url: url });
            setMediaPickerOpen(null);
          }}
          onClose={() => setMediaPickerOpen(null)}
          profile={profile}
          title={mediaPickerOpen === "logo" ? "Pick Logo" : "Pick Icon"}
        />
      )}
    </div>
  );
}

// ── BrandDefaultsEditor + TopicDistributionEditor — Content Agent topic mix editors (used by content-engine module + Plan Dashboard) ──
export function BrandDefaultsEditor({ value, onChange }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.t2, marginBottom: 6 }}>Brand Defaults (seed every new workspace on this plan)</div>
      <div style={{ fontSize: 11, color: C.t3, marginBottom: 10 }}>Logo and icon are intentionally per-business and excluded.</div>
      <BrandKitForm
        brandKit={value || {}}
        onChange={(patch) => onChange({ ...(value || {}), ...patch })}
        onLogoReplace={() => { /* defaults never carry logo */ }}
        workspaceId={null}
        wsMeta={{}}
        profile={null}
      />
    </div>
  );
}

export function TopicDistributionEditor({ value, inheritedFrom, onChange, mountLabel }) {
  // value === null means "inherit". The editor still needs something to render —
  // when null, show the inherited list but disable editing (slider visible, all
  // interactions enabled require switching off "inherit"). For simplicity here:
  // if value is null, render inheritedFrom as if it were the current value but
  // also show an "Inherited from plan" badge. First edit switches to custom mode.
  const effective = value ?? inheritedFrom ?? SEED_TOPIC_CATEGORIES;
  const isCustom  = value != null;
  const [dragIdx, setDragIdx] = useState(null);
  const [adding, setAdding]   = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newHint, setNewHint]   = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const totalWeight = effective.reduce((a, c) => a + (c.weight || 0), 0);
  const error = validateTopicCategories(effective);
  const showResetBtn = isCustom && inheritedFrom;

  const emit = (next) => onChange(next);
  const switchToCustomIfNeeded = (next) => { onChange(next); };

  const handleWeight = (idx, w) => {
    const next = effective.map((c, i) => i === idx ? { ...c, weight: Math.max(0, Math.min(100, Math.round(w))) } : c);
    switchToCustomIfNeeded(next);
  };
  const handleLabel = (idx, label) => {
    const next = effective.map((c, i) => i === idx ? { ...c, label } : c);
    switchToCustomIfNeeded(next);
  };
  const handleHint = (idx, hint) => {
    const next = effective.map((c, i) => i === idx ? { ...c, hint } : c);
    switchToCustomIfNeeded(next);
  };
  const handleDelete = (idx) => {
    const next = effective.filter((_, i) => i !== idx);
    switchToCustomIfNeeded(next);
    setConfirmDeleteId(null);
  };
  const handleAdd = () => {
    if (!newLabel.trim() || !newHint.trim()) return;
    const existing = new Set(effective.map(c => c.id));
    const id = slugifyTopicCategoryId(newLabel.trim(), existing);
    const next = [...effective, { id, label: newLabel.trim(), weight: 0, hint: newHint.trim() }];
    switchToCustomIfNeeded(next);
    setNewLabel(""); setNewHint(""); setAdding(false);
  };
  const handleNormalise = () => switchToCustomIfNeeded(normaliseTopicWeights(effective));
  const handleReset = () => emit(null);

  const handleDragStart = (i) => setDragIdx(i);
  const handleDragOver  = (e) => { e.preventDefault(); };
  const handleDrop = (i) => {
    if (dragIdx == null || dragIdx === i) { setDragIdx(null); return; }
    const next = effective.slice();
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    setDragIdx(null);
    switchToCustomIfNeeded(next);
  };

  return (
    <div style={{ ...S.card, padding: 20, marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Topic Mix</div>
          <div style={{ fontSize: 12, color: C.t3, marginTop: 3 }}>
            Decide how your AI splits the 18 topics it generates each plan.
          </div>
          <div style={{ marginTop: 6 }}>
            <span style={{
              fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 5,
              background: isCustom ? "#eef2ff" : "#f5f3ff",
              border: `1px solid ${isCustom ? "#c7d2fe" : "#e9d5ff"}`,
              color:   isCustom ? "#4338ca" : "#8b5cf6",
            }}>{isCustom ? "Custom for this " + (mountLabel || "workspace") : "Inherited from plan"}</span>
          </div>
        </div>
        {showResetBtn && (
          <button onMouseDown={e => e.preventDefault()} onClick={handleReset} style={{ ...S.btnGhost, fontSize: 12 }}>
            Reset to plan default
          </button>
        )}
      </div>

      {effective.map((c, idx) => (
        <div key={c.id}
          draggable
          onDragStart={() => handleDragStart(idx)}
          onDragOver={handleDragOver}
          onDrop={() => handleDrop(idx)}
          style={{
            display: "flex", alignItems: "center", gap: 10, padding: "8px 0",
            borderTop: idx === 0 ? `1px solid ${C.border}` : "none",
            borderBottom: `1px solid ${C.border}`,
            opacity: dragIdx === idx ? 0.4 : 1,
          }}>
          <span style={{ cursor: "grab", color: C.t4, fontSize: 16, userSelect: "none" }}>⋮⋮</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <input
              defaultValue={c.label}
              onBlur={e => handleLabel(idx, e.target.value)}
              style={{ fontSize: 13, fontWeight: 700, color: C.text, background: "transparent", border: "none", outline: "none", width: "100%", padding: 0 }}
            />
            <input
              defaultValue={c.hint}
              onBlur={e => handleHint(idx, e.target.value)}
              style={{ fontSize: 11, color: C.t3, background: "transparent", border: "none", outline: "none", width: "100%", padding: 0, marginTop: 2 }}
            />
          </div>
          <input type="range" min={0} max={100} value={c.weight}
            onChange={e => handleWeight(idx, Number(e.target.value))}
            style={{ width: 140 }} />
          <div style={{ width: 40, textAlign: "right", fontSize: 13, fontWeight: 700, color: C.text }}>{c.weight}%</div>
          {confirmDeleteId === c.id ? (
            <div style={{ display: "flex", gap: 4 }}>
              <button onMouseDown={e => e.preventDefault()} onClick={() => handleDelete(idx)} style={{ color: C.danger, borderColor: `${C.danger}40`, ...S.btnGhost, padding: "3px 8px", fontSize: 11 }}>Yes</button>
              <button onMouseDown={e => e.preventDefault()} onClick={() => setConfirmDeleteId(null)} style={{ ...S.btnGhost, padding: "3px 8px", fontSize: 11 }}>No</button>
            </div>
          ) : (
            <button onMouseDown={e => e.preventDefault()} onClick={() => setConfirmDeleteId(c.id)} style={{ color: C.danger, ...S.btnGhost, padding: "3px 8px", fontSize: 11 }}>🗑</button>
          )}
        </div>
      ))}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12 }}>
        {adding ? (
          <div style={{ display: "flex", gap: 6, flex: 1 }}>
            <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Label (e.g. Brand / Story)"
              style={{ ...S.input, fontSize: 12, padding: "5px 8px", flex: 1 }} />
            <input value={newHint} onChange={e => setNewHint(e.target.value)} placeholder="Hint for AI (short examples)"
              style={{ ...S.input, fontSize: 12, padding: "5px 8px", flex: 2 }} />
            <button onMouseDown={e => e.preventDefault()} onClick={handleAdd} disabled={!newLabel.trim() || !newHint.trim()} style={{ ...S.btn, padding: "5px 12px", fontSize: 12, background: "#6366f1" }}>Add</button>
            <button onMouseDown={e => e.preventDefault()} onClick={() => { setAdding(false); setNewLabel(""); setNewHint(""); }} style={{ ...S.btnGhost, padding: "5px 12px", fontSize: 12 }}>Cancel</button>
          </div>
        ) : (
          <button onMouseDown={e => e.preventDefault()} onClick={() => setAdding(true)} style={{ ...S.btnGhost, fontSize: 12 }}>+ Add category</button>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {totalWeight !== 100 && (
            <button onMouseDown={e => e.preventDefault()} onClick={handleNormalise} style={{ ...S.btnGhost, fontSize: 11 }}>Normalise</button>
          )}
          <span style={{
            fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 5,
            background: totalWeight === 100 ? "#f0fdf4" : "#fef3c7",
            color:      totalWeight === 100 ? "#166534" : "#92400e",
            border: `1px solid ${totalWeight === 100 ? "#bbf7d0" : "#fde68a"}`,
          }}>Total: {totalWeight}</span>
        </div>
      </div>

      {error && (
        <div style={{ marginTop: 10, fontSize: 12, color: C.danger }}>{error}</div>
      )}
    </div>
  );
}

// ── HelpIcon + CategoryBadge — Lead Agent helpers (shared: lead-agent module + Inbox lead_reply card) ──
export function HelpIcon({ section, setTab, navTo }) {
  return (
    <span
      onMouseDown={e => e.preventDefault()}
      onClick={(e) => {
        e.stopPropagation();
        sessionStorage.setItem("lead-agent-help-section", section);
        if (setTab) setTab("help");
        else if (navTo) navTo("lead-agent");
      }}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 14, height: 14, borderRadius: "50%", background: C.s3, color: C.t3,
        fontSize: 9, fontWeight: 700, cursor: "pointer", marginLeft: 6,
        verticalAlign: "middle", transition: "background 0.15s, color 0.15s" }}
      onMouseEnter={e => { e.currentTarget.style.background = "#e0e7ff"; e.currentTarget.style.color = "#6366f1"; }}
      onMouseLeave={e => { e.currentTarget.style.background = C.s3; e.currentTarget.style.color = C.t3; }}
      title="Help"
    >?</span>
  );
}

export function CategoryBadge({ category, confidence }) {
  const c = CATEGORY_BADGE[category];
  if (!c) return null;
  const pct = typeof confidence === "number" ? Math.round(confidence * 100) : null;
  return (
    <span style={{ background: c.bg, color: c.fg, fontSize: 11, fontWeight: 700,
      padding: "2px 8px", borderRadius: 10, display: "inline-flex", alignItems: "center", gap: 4 }}>
      {c.emoji} {c.label}{pct != null && <span style={{ fontWeight: 400, opacity: 0.7 }}> ({pct}%)</span>}
    </span>
  );
}
